import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import config from "config/config";
import { instance } from "api/apiBase";
import { CheckoutForm } from "./components/CheckoutForm";
import { InfoCircle } from "untitledui-js-base";
import { Step } from "../../types";
import { StepNames } from "../../constants";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { CheckoutSubmissionType, Money, SubmissionType } from "shared/types";
import { useIntercom } from "react-use-intercom";
import { Loader } from "shared/components/Loader";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import { Clock01Icon } from "@hugeicons-pro/core-solid-rounded";

const stripe = loadStripe(config.stripePk || "");

interface PaymentIntent {
  client_secret: string;
  ephemeral_key: string;
  payment_intent_id: string;
  stripe_customer_id: string;
}

export const CheckoutSlice = ({
  applicationFee,
  desiredMoveInDate,
  applicationId,
  applicantId,
  ineligibleToCheckout,
  sendDisclosureEmail,
  isSubmissionDisclosureFinished,
  steps,
  setActiveStepName,
  preferredCheckoutType,
  hasIncomeSource,
  goBack,
  onSuccess
}: {
  applicationFee?: Money | null;
  desiredMoveInDate?: string | null;
  applicationId: number;
  applicantId: number;
  ineligibleToCheckout?: string;
  steps?: Record<string, Step>;
  setActiveStepName: (stepName: string) => void;
  hasIncomeSource?: boolean;
  goBack: () => void;
  onSuccess: () => void;
  preferredCheckoutType?: CheckoutSubmissionType;
  sendDisclosureEmail: boolean;
  isSubmissionDisclosureFinished: boolean;
}) => {
  const intercom = useIntercom();

  const queryClient = useQueryClient();
  // Create payment intent
  const { data: paymentIntent, isLoading } = useQuery({
    queryKey: ["createPaymentIntent", applicantId],
    queryFn: () =>
      instance
        .post<PaymentIntent>(
          `/portal/v1/applicants/${applicantId}/checkout/create_payment_intent`
        )
        .then(({ data }) => data),
    enabled: Boolean(applicantId) && Boolean(applicationId)
  });

  // Checkout
  const checkOutMeta = useMutation({
    mutationKey: ["checkOut", applicantId],
    mutationFn: (data: { stripe_token: string }) =>
      instance.post(
        `/portal/v1/applicants/${applicantId}/checkout/with_stripe_payment_intent`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(applicationId)]
      });
      onSuccess();
    }
  });

  const freeCheckoutMeta = useMutation({
    mutationKey: ["freeCheckout", applicationId, applicantId],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/submit_application`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(applicationId)]
      });
      onSuccess();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  // If some steps are unfished show the checkout error
  const unfinishedSteps = Object.entries(steps || {}).reduce((acc, s) => {
    const step = s[1];

    // Ignore income_documents
    if (!hasIncomeSource) if (s[0] === "income_documents") return acc;

    if (s[0] === "move_in_date") {
      if (!desiredMoveInDate) acc[s[0]] = step;
      return acc;
    }

    if (!Object.keys(step.data).length && s[0] !== "checkout") acc[s[0]] = step;

    return acc;
  }, {});

  const hasUnfinishedSteps = Object.values(unfinishedSteps).length;

  if (hasUnfinishedSteps)
    return (
      <div className="p-8">
        <h1 className="text-sm font-medium mb-2">
          Please complete the following steps to finish your application and
          proceed to check out.
        </h1>

        <div className="space-y-2">
          {Object.keys(unfinishedSteps).map((s) => {
            return (
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.XS}
                key={s}
                className="!block"
                onClick={() => {
                  setActiveStepName(s);
                }}
              >
                {StepNames[s]}
              </Button>
            );
          })}
        </div>
      </div>
    );

  if (sendDisclosureEmail && !isSubmissionDisclosureFinished)
    return (
      <div className="p-8 h-[calc(80vh-73px)] flex items-center justify-center bg-background-900 rounded-br-lg">
        <div>
          <div className="flex justify-center mb-6">
            <HugeiconsIcon
              icon={Clock01Icon}
              size={48}
              color="var(--primary-500)"
            />
          </div>

          <h1 className="text-lg font-bold text-content-1000 mb-2 text-center">
            Waiting for applicant to sign disclosure
          </h1>

          <p className="text-base text-content-400 text-center">
            The disclosure has been sent. Checkout will be available once
            it&apos;s signed
          </p>
        </div>
      </div>
    );

  if (
    applicationFee?.cents === 0 ||
    preferredCheckoutType === SubmissionType.CheckoutPaidInCash
  )
    return (
      <div className="bg-background-900">
        <div className="max-w-[400px] mx-auto flex h-[calc(80vh-154px)] items-center justify-center">
          <div className="">
            <div className="h-10 w-10 shrink-0 flex items-center justify-center mx-auto bg-primary-500 rounded-full mb-6">
              <span className="text-2xl font-bold text-center text-white">
                !
              </span>
            </div>

            <h1 className="text-lg font-bold text-content-1000 mb-2 text-center">
              After this screen, you will not be able to edit the previously
              entered data
            </h1>
            <p className="text-base text-content-400 text-center">
              Please confirm that all the data you entered is correct and that
              you are ready to proceed
            </p>

            <Show
              if={preferredCheckoutType === SubmissionType.CheckoutPaidInCash}
            >
              <p className="text-base font-medium text-content-600 text-center mt-4">
                Submit application only if applicant has already paid in cash.
              </p>
            </Show>
          </div>
        </div>

        <div className="bg-white mt-auto py-4 px-6 flex justify-end border-t border-alpha-100 gap-2">
          <Button className="w-[125px]" onClick={goBack}>
            Go back
          </Button>
          <Button
            className="w-[125px]"
            variant={ButtonVariant.PRIMARY}
            loading={freeCheckoutMeta.isPending}
            onClick={() => {
              freeCheckoutMeta.mutateAsync();
            }}
          >
            Accept
          </Button>
        </div>
      </div>
    );

  // Should'nt be possible, but just in case
  if (!applicantId || !paymentIntent) return null;

  if (isLoading)
    return (
      <div className="mt-20 mx-auto">
        <Loader />
      </div>
    );

  if (!paymentIntent.client_secret)
    return (
      <div className="p-8 h-[calc(80vh-73px)] flex items-center justify-center">
        <div>
          <div className="h-20 w-20 flex items-center justify-center mx-auto bg-danger-50 rounded-full">
            <InfoCircle className="stroke-danger-500 h-10 w-10 rotate-180" />
          </div>

          <h1 className="text-xl font-bold text-center mt-4">
            Unable to checkout
          </h1>
          <p className="text-gray-500 text-center mt-2 text-base">
            {ineligibleToCheckout ||
              "Please make sure there are no errors in identity steps"}
          </p>

          <Button
            variant={ButtonVariant.PRIMARY}
            className="mx-auto mt-10"
            onClick={() => {
              intercom.show();
            }}
          >
            Contact support
          </Button>
        </div>
      </div>
    );

  return (
    <div className="p-6 max-w-[460px] w-full relative mx-auto h-[calc(80vh-100px)] overflow-y-auto">
      <Elements
        options={{
          clientSecret: paymentIntent.client_secret,
          locale: "en"
        }}
        stripe={stripe}
      >
        <CheckoutForm
          checkout={(intentId) => {
            return checkOutMeta.mutateAsync({ stripe_token: intentId });
          }}
        />
      </Elements>
    </div>
  );
};
